'use client'
import NavigateRightIcon from '@/app/icons/navigate-right'
import Attribution from '@/components/attribution-v2'

import { VideoSpotlightDecision } from '@/app/api/placements/types'
import { useIsMobile } from '@/app/util/use-media-query'
import { AttrOverlay } from '@/components/grid/overlay/attr-overlay'
import { fireAdTrackingPixel, handleAdCtaClick, useAdMediaClick } from '@/util/ad-click'
import { cn } from '@/util/cn'
import { GifOverlayProps, Video, VolumeOffIcon, VolumeOnIcon } from '@giphy/react-components'
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { spotlightWidth } from '../ad-grid/spotlight-util'

function useAttentionPauseResume(opts: { onPause?: () => void; onResume?: () => void; trackViewport?: boolean }) {
    const { onPause, onResume, trackViewport } = opts
    const containerRef = useRef<HTMLDivElement | null>(null)
    const activeRef = useRef(true)

    const setActive = useCallback(
        (next: boolean) => {
            if (next === activeRef.current) return
            activeRef.current = next
            next ? onResume?.() : onPause?.()
        },
        [onPause, onResume]
    )

    useEffect(() => {
        const onVis = () => setActive(!document.hidden)
        const onFocus = () => setActive(true)
        const onBlur = () => setActive(false)
        const onPageHide = () => setActive(false)

        document.addEventListener('visibilitychange', onVis)
        window.addEventListener('focus', onFocus)
        window.addEventListener('blur', onBlur)
        window.addEventListener('pagehide', onPageHide)

        let io: IntersectionObserver | undefined
        if (trackViewport && containerRef.current) {
            io = new IntersectionObserver(
                (entries) => {
                    const e = entries[0]
                    setActive(e.isIntersecting && e.intersectionRatio > 0)
                },
                { threshold: [0] }
            )
            io.observe(containerRef.current)
        }

        return () => {
            document.removeEventListener('visibilitychange', onVis)
            window.removeEventListener('focus', onFocus)
            window.removeEventListener('blur', onBlur)
            window.removeEventListener('pagehide', onPageHide)
            io?.disconnect()
        }
    }, [setActive, trackViewport])

    return { containerRef }
}

const GridOverlay = ({
    gif,
    data,
    muted,
    toggleMuted,
    onMediaClick,
}: GifOverlayProps & {
    data: VideoSpotlightDecision
    muted: boolean
    toggleMuted: () => void
    onMediaClick: (e: SyntheticEvent) => void
}) => {
    const onCtaClick = (e: SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation()
        handleAdCtaClick({
            ctaTrackingUrl: data.cta_tracking_url,
            ctaUrl: data.cta_url,
        })
    }
    const isMobile = useIsMobile()
    return (
        <div className="absolute inset-0 isolate cursor-pointer">
            <div className="absolute inset-0" data-testid="video-spotlight-media-click-target" onClick={onMediaClick} />
            {isMobile ? (
                <AttrOverlay gif={gif} isHovered={true} width={spotlightWidth} />
            ) : (
                <Attribution user={gif.user} className="absolute bottom-2 left-2 text-shadow-lg/10" />
            )}
            <div
                className="absolute top-3 right-3 cursor-pointer transition-transform hover:scale-125"
                onClick={toggleMuted}
                role="button"
                aria-label={muted ? 'Unmute' : 'Mute'}
                aria-pressed={muted}
            >
                {muted ? <VolumeOffIcon size={32} /> : <VolumeOnIcon size={32} />}
            </div>
            {data?.cta_url && (
                <div
                    data-testid="video-spotlight-cta"
                    className={cn(
                        'absolute -right-1 bottom-2 flex h-[32px] cursor-pointer items-center justify-center gap-2 rounded-md px-5 font-bold text-giphyWhite/75 hover:underline',
                        !isMobile && 'right-2 h-[50px] bg-giphyBlack/50'
                    )}
                    onClick={onCtaClick}
                >
                    {data.cta_text} <NavigateRightIcon className="size-3 fill-giphyWhite/75" />
                </div>
            )}
        </div>
    )
}

const VideoSpotlight = ({ data, className }: { data: VideoSpotlightDecision; className?: string }) => {
    const {
        gif,
        analytics,
        impression_tracking_url: impression_url,
        click_tracking_url,
        click_url,
        third_party_tag,
    } = data
    const onAdMediaClick = useAdMediaClick()
    const [muted, setMuted] = useState(true)
    const [loopCount, setLoopCount] = useState(0)
    const hasCompletedFirstLoopRef = useRef(false)

    const { containerRef } = useAttentionPauseResume({
        onPause: () => {
            if (analytics?.pause) fireAdTrackingPixel(analytics.pause, 'pause')
        },
        onResume: () => {
            if (analytics?.resume) fireAdTrackingPixel(analytics.resume, 'resume')
        },
        trackViewport: false,
    })

    const handleCanPlay = () => {
        if (hasCompletedFirstLoopRef.current) {
            return
        }
        if (analytics?.start) fireAdTrackingPixel(analytics.start, 'start')
    }

    const handleFirstPlay = () => {
        fireAdTrackingPixel(impression_url)
        fireAdTrackingPixel(third_party_tag)
    }

    const [userMuted, setUserMuted] = useState<boolean>(false)
    useEffect(() => {
        if (!userMuted) {
            return
        }
        if (muted) {
            if (analytics?.mute) fireAdTrackingPixel(analytics.mute, 'mute')
        } else {
            if (analytics?.unmute) fireAdTrackingPixel(analytics.unmute, 'unmute')
        }
    }, [muted, analytics, userMuted])

    const handleQuartile = (q: 0.25 | 0.5 | 0.75) => {
        if (hasCompletedFirstLoopRef.current) {
            return
        }
        if (q === 0.25 && analytics?.firstq) fireAdTrackingPixel(analytics.firstq, 'firstq')
        if (q === 0.5 && analytics?.midpoint) fireAdTrackingPixel(analytics.midpoint, 'midpoint')
        if (q === 0.75 && analytics?.thirdq) fireAdTrackingPixel(analytics.thirdq, 'thirdq')
    }

    const handleEnd = () => {
        if (loopCount == 1) {
            if (analytics?.complete) fireAdTrackingPixel(analytics.complete, 'complete')
        }
    }

    const handleLoop = (loopCount: number) => {
        hasCompletedFirstLoopRef.current = true
        setLoopCount(loopCount)
    }

    const Controls = useMemo(() => {
        const Overlay = (props: GifOverlayProps) => {
            return (
                <GridOverlay
                    {...props}
                    data={data}
                    muted={muted}
                    onMediaClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onAdMediaClick({
                            clickTrackingUrl: click_tracking_url,
                            clickUrl: click_url,
                            gifUrl: gif?.url ?? '',
                        })
                    }}
                    toggleMuted={() => {
                        setUserMuted(true)
                        setMuted((m) => !m)
                    }}
                />
            )
        }
        Overlay.displayName = 'GridOverlayControls'
        return Overlay
    }, [click_tracking_url, click_url, data, gif?.url, muted, onAdMediaClick])

    if (!gif) {
        return null
    }
    const aspectRatio = gif.images.original.width / gif.images.original.height
    return (
        <div key={gif.id} ref={containerRef} className={cn('relative', className)}>
            <Video
                // eslint-disable-next-line better-tailwindcss/no-unregistered-classes
                className={cn('giphy-video-spotlight rounded-sm', className)}
                gif={gif}
                width={spotlightWidth}
                percentWidth="100%"
                muted={muted}
                controls
                hideAttribution
                hideMute
                hideTitle
                overlay={Controls}
                style={{ aspectRatio }}
                onCanPlay={handleCanPlay}
                onFirstPlay={handleFirstPlay}
                onQuartile={handleQuartile}
                onEnded={handleEnd}
                onLoop={handleLoop}
            />
        </div>
    )
}
export default VideoSpotlight
